import {
  Component,
  input,
  output,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { EntityId } from '@ngrx/signals/entities';
import { M3ButtonComponent } from '@shared/button';
import { TextBlockCategory } from '../models/text-block-category.model';

@Component({
  selector: 'app-text-block-categories-grid',
  standalone: true,
  imports: [
    CommonModule,
    MatGridListModule,
    MatCardModule,
    MatIconModule,
    MatCheckboxModule,
    MatChipsModule,
    CdkDropList,
    CdkDrag,
    M3ButtonComponent,
  ],
  template: `
    <div class="grid-container">
      @if (isLoading()) {
        <div class="loading-indicator">
          <mat-icon>refresh</mat-icon>
          Lade Textkategorien...
        </div>
      }
      @if (categories().length === 0 && !isLoading()) {
        <div class="empty-state">
          <mat-icon>category</mat-icon>
          <p>Keine Textkategorien vorhanden.</p>
        </div>
      }
      @if (categories().length > 0) {
        <div
          class="categories-grid"
          cdkDropList
          (cdkDropListDropped)="onDrop($event)"
          [cdkDropListDisabled]="!reorderMode()"
          [style.grid-template-columns]="gridTemplateColumns()"
        >
          @for (category of categories(); track category.id) {
            <div class="grid-item" cdkDrag [cdkDragDisabled]="!reorderMode()">
              <mat-card
                class="category-card"
                [class.selected]="selectedIds().includes(category.id)"
                [class.reorder-card]="reorderMode()"
              >
                <mat-card-header>
                  <div class="card-header-content">
                    @if (!reorderMode()) {
                      <mat-checkbox
                        [checked]="selectedIds().includes(category.id)"
                        (change)="onToggleSelection(category.id)"
                        class="selection-checkbox"
                      >
                      </mat-checkbox>
                    } @else {
                      <mat-icon class="drag-handle">drag_indicator</mat-icon>
                    }
                  </div>
                </mat-card-header>

                <mat-card-content>
                  <h3 class="category-name">{{ category.name }}</h3>

                  <div class="category-details">
                    <div class="detail-row highlight">
                      <span class="label">Textbausteine:</span>
                      <span class="value count">{{
                        category.textBlockCount
                      }}</span>
                    </div>
                    @if (category.updatedAt) {
                      <div class="detail-row">
                        <span class="label">Geändert:</span>
                        <span class="value">{{
                          category.updatedAt | date: 'dd.MM.yyyy HH:mm'
                        }}</span>
                      </div>
                    }
                  </div>
                </mat-card-content>

                @if (!reorderMode()) {
                  <mat-card-actions align="end">
                    <app-m3-button
                      variant="standard"
                      iconLeft="edit"
                      [disabled]="isUpdating()"
                      (clicked)="onEdit(category)"
                    >
                      {{ isUpdating() ? 'Bearbeite...' : 'Bearbeiten' }}
                    </app-m3-button>
                    <app-m3-button
                      variant="standard"
                      iconLeft="delete"
                      [disabled]="isUpdating()"
                      (clicked)="onDelete(category)"
                    >
                      Löschen
                    </app-m3-button>
                  </mat-card-actions>
                }
              </mat-card>
            </div>
          }
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .grid-container {
        height: 100%;
        overflow-y: auto;
        padding: 0 24px;
      }

      .categories-grid {
        margin-top: 16px;
        display: grid;
        gap: 16px;
        grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
      }

      .grid-item {
        height: 320px;
      }

      .category-card {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
      }

      .category-card mat-card-content {
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      .category-card.selected {
        border: 2px solid var(--md-sys-color-primary);
      }

      .card-header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
      }

      .selection-checkbox {
        margin-right: auto;
      }

      .category-name {
        font-size: 18px;
        font-weight: 500;
        margin: 16px 0 12px 0;
        color: var(--md-sys-color-on-surface);
        line-height: 1.2;
      }

      .category-details {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
      }

      .detail-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 8px;
        font-size: 14px;
      }

      .detail-row.highlight {
        background-color: var(--md-sys-color-primary-container);
        padding: 8px 12px;
        border-radius: 8px;
        margin-bottom: 16px;
      }

      .detail-row .label {
        color: var(--md-sys-color-on-surface-variant);
        font-weight: 500;
      }

      .detail-row.highlight .label {
        color: var(--md-sys-color-on-primary-container);
        font-weight: 600;
      }

      .detail-row .value {
        color: var(--md-sys-color-on-surface);
        text-align: right;
      }

      .detail-row.highlight .value.count {
        color: var(--md-sys-color-on-primary-container);
        font-weight: 700;
        font-size: 16px;
      }

      mat-card-actions {
        padding: 8px 16px;
        margin: 0;
        margin-top: auto;
      }

      mat-card-actions button {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        min-width: auto;
      }

      mat-card-actions button mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
      }

      .loading-indicator,
      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 60px 20px;
        color: var(--md-sys-color-on-surface-variant);
        gap: 16px;
      }

      .loading-indicator mat-icon,
      .empty-state mat-icon {
        font-size: 48px;
        width: 48px;
        height: 48px;
        opacity: 0.5;
      }

      .reorder-card {
        cursor: grab;
      }

      .reorder-card:active {
        cursor: grabbing;
      }

      .drag-handle {
        cursor: grab;
        color: var(--md-sys-color-on-surface-variant);
      }

      .drag-handle:hover {
        color: var(--md-sys-color-on-surface);
      }

      .cdk-drag-preview {
        box-sizing: border-box;
        border-radius: 16px;
        box-shadow:
          0 5px 5px -3px rgba(0, 0, 0, 0.2),
          0 8px 10px 1px rgba(0, 0, 0, 0.14),
          0 3px 14px 2px rgba(0, 0, 0, 0.12);
      }

      .cdk-drag-animating {
        transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
      }

      .cdk-drop-list-dragging .grid-item:not(.cdk-drag-placeholder) {
        transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
      }

      @media (max-width: 600px) {
        .grid-container {
          padding: 0 16px;
        }
      }
    `,
  ],
})
export class TextBlockCategoriesGridComponent {
  private readonly breakpointObserver = inject(BreakpointObserver);

  categories = input.required<TextBlockCategory[]>();
  selectedIds = input.required<EntityId[]>();
  isLoading = input.required<boolean>();
  isUpdating = input.required<boolean>();
  reorderMode = input.required<boolean>();

  toggleSelection = output<number>();
  editCategory = output<TextBlockCategory>();
  deleteCategory = output<TextBlockCategory>();
  reorderCategories = output<TextBlockCategory[]>();

  private readonly isXSmall = toSignal(
    this.breakpointObserver.observe('(max-width: 599px)')
  );
  private readonly isSmall = toSignal(
    this.breakpointObserver.observe('(max-width: 959px)')
  );
  private readonly isMedium = toSignal(
    this.breakpointObserver.observe('(max-width: 1279px)')
  );

  protected readonly gridTemplateColumns = computed(() => {
    if (this.isXSmall()?.matches) return 'repeat(1, minmax(320px, 1fr))';
    if (this.isSmall()?.matches) return 'repeat(2, minmax(320px, 1fr))';
    if (this.isMedium()?.matches) return 'repeat(3, minmax(320px, 1fr))';
    return 'repeat(4, minmax(320px, 1fr))';
  });

  trackByCategory(_index: number, category: TextBlockCategory): number {
    return category.id;
  }

  onToggleSelection(categoryId: number): void {
    this.toggleSelection.emit(categoryId);
  }

  onEdit(category: TextBlockCategory): void {
    this.editCategory.emit(category);
  }

  onDelete(category: TextBlockCategory): void {
    this.deleteCategory.emit(category);
  }

  onDrop(event: CdkDragDrop<TextBlockCategory[]>): void {
    const categories = [...this.categories()];
    moveItemInArray(categories, event.previousIndex, event.currentIndex);
    this.reorderCategories.emit(categories);
  }
}
